import React from "react";
import { Box, IconButton, Text, VStack, HStack, Tooltip } from "@chakra-ui/react";
import { connect } from "react-redux";
import { TbCheck, TbX, TbPlus, TbMinus } from "react-icons/tb";

// Helper components for consistency
const CorrectionCard = ({ data, onAccept, onReject, moveToPosition }) => {
  const isAdded = data.className.indexOf("Ace_added_marker") === 0;
  const mode = isAdded ? "Added" : "Deleted";
  const accentColor = isAdded ? "green.400" : "red.400";
  const bgColor = "#2d2d2d"; // VS Code-like dark card bg
  const icon = isAdded ? <TbPlus /> : <TbMinus />;

  return (
    <Box
      w="100%"
      bg={bgColor}
      borderLeftWidth="4px"
      borderLeftColor={accentColor}
      borderRadius="sm"
      boxShadow="md"
      mb={3}
      transition="all 0.2s"
      _hover={{ transform: "translateX(2px)", boxShadow: "lg" }}
      display="flex"
      flexDirection="column"
    >
      <HStack
        p={2}
        bg="rgba(255, 255, 255, 0.05)"
        justifyContent="space-between"
        borderTopRightRadius="sm"
      >
        <HStack spacing={2}>
          <Box color={accentColor} fontSize="lg">
            {icon}
          </Box>
          <Text
            fontSize="xs"
            fontWeight="bold"
            textTransform="uppercase"
            letterSpacing="wider"
            color={accentColor}
          >
            {mode}
          </Text>
        </HStack>
        <HStack spacing={1}>
          <Tooltip label="Accept" hasArrow>
            <IconButton
              icon={<TbCheck />}
              size="xs"
              variant="ghost"
              colorScheme="green"
              color="green.400"
              aria-label="Accept change"
              onClick={(e) => {
                e.stopPropagation();
                onAccept(data);
              }}
              _hover={{ bg: "green.900", color: "green.200" }}
            />
          </Tooltip>
          <Tooltip label="Reject" hasArrow>
            <IconButton
              icon={<TbX />}
              size="xs"
              variant="ghost"
              colorScheme="red"
              color="red.400"
              aria-label="Reject change"
              onClick={(e) => {
                e.stopPropagation();
                onReject(data);
              }}
              _hover={{ bg: "red.900", color: "red.200" }}
            />
          </Tooltip>
        </HStack>
      </HStack>

      <Box p={3} cursor="pointer" onClick={() => moveToPosition(data)}>
        <Text
          fontSize="sm"
          fontFamily="'Fira Code', monospace" // Coding font for diffs
          color="gray.200"
          wordBreak="break-word"
          textDecoration={!isAdded ? "line-through" : "none"}
          opacity={!isAdded ? 0.7 : 1}
        >
          {data.text}
        </Text>
      </Box>
    </Box>
  );
};

const Corrections = (props) => {
  const moveToPosition = (a) => {
    if (props.ace.current && props.ace.current.editor) {
      if (!a.endCol) {
        props.ace.current.editor.gotoLine(a.startRow + 1, a.startCol);
      } else {
        props.ace.current.editor.session.selection.setRange({
          start: { row: a.startRow, column: a.startCol },
          end: { row: a.endRow, column: a.endCol },
        });
      }
      props.ace.current.editor.scrollToLine(a.startRow, true);
    }
  };

  return (
    <VStack
      overflowY="auto"
      overflowX="hidden"
      height="100%"
      p={2}
      spacing={0}
      align="stretch"
      css={{
        "&::-webkit-scrollbar": { width: "8px" },
        "&::-webkit-scrollbar-track": { background: "transparent" },
        "&::-webkit-scrollbar-thumb": { background: "#555", borderRadius: "4px" },
        "&::-webkit-scrollbar-thumb:hover": { background: "#777" },
      }}
    >
      {props.corrections && props.corrections.length > 0 ? (
        props.corrections.map((item, index) => (
          <CorrectionCard
            key={index}
            data={item}
            onAccept={props.acceptCorrections}
            onReject={props.declineCorrections}
            moveToPosition={moveToPosition}
          />
        ))
      ) : (
        <Box textAlign="center" py={10} px={6}>
          <Text color="gray.500" fontSize="sm">
            No track changes found.
          </Text>
        </Box>
      )}
    </VStack>
  );
};

const mapStateToProps = (state, ownProps) => ({
  corrections: state.correction.corrections,
  ace: ownProps.ace,
});

const mapDispatchToProps = (dispatch) => {
  return {
    acceptCorrections: (data) => dispatch({ type: "accept", payload: data }),
    declineCorrections: (data) => dispatch({ type: "reject", payload: data }),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Corrections);
